/**
 * Per-metric scope / SKU picks.
 * Active logs follows /logs Groups + SKU ticks. Other metrics keep their own picks.
 */

import {
  ActivityGroupsModel,
  ActivitySkuHierarchyGroup,
  ALL_ACTIVITY_SCOPE_ID,
  collectScopeOccurrenceKeys,
  collectSkuOccurrenceKeys,
  DEPARTMENTS_METRIC_SCOPE_ID,
  SHOW_ALL_ACTIVITY_LOGS,
} from 'pages/Logs/activityGroupsModel';
import { MetricsKey } from './metricsKeys';

export type MetricsScopeSelection = {
  scopesChecked: string[],
  skuChecked: string[],
  /** 'sku' = outer pie / series are SKUs under the pick (Active logs). */
  outerKind?: 'sku' | 'scope',
};

const MAX_CUSTOM_BALANCE_SCOPES = 3;

const selectionForParentScope = (
  model: ActivityGroupsModel,
  parentScopeId: string,
): MetricsScopeSelection | null => {
  const findNode = (
    nodes: ActivitySkuHierarchyGroup[],
    parentPath: string[],
  ): ActivitySkuHierarchyGroup | null => {
    for (let i = 0; i < (nodes || []).length; i += 1) {
      const node = nodes[i];
      if (node.isObjectGroup) continue;
      if (node.id === parentScopeId) return node;
      const found = findNode(
        (node.children || []).filter((c) => !c.isObjectGroup),
        [...parentPath, node.id],
      );
      if (found) return found;
    }
    return null;
  };
  const node = findNode(model.skuHierarchy, []);
  if (!node) return null;
  const parentPath: string[] = [];
  return {
    scopesChecked: collectScopeOccurrenceKeys(node, parentPath),
    skuChecked: collectSkuOccurrenceKeys(node, parentPath),
  };
};

/** Compact token — filter expands via department index (see filterMetricsScopedLogs). */
export const departmentMetricsScopeSelection = (): MetricsScopeSelection => ({
  scopesChecked: [DEPARTMENTS_METRIC_SCOPE_ID],
  skuChecked: [SHOW_ALL_ACTIVITY_LOGS],
  outerKind: 'scope',
});

/** Active logs — same Groups + SKU ticks as /logs; charts break down by SKU. */
export const logsMetricsScopeSelection = (
  scopesChecked: string[],
  skuChecked: string[],
): MetricsScopeSelection => ({
  scopesChecked: (scopesChecked || []).length ? scopesChecked : [ALL_ACTIVITY_SCOPE_ID],
  skuChecked: (skuChecked || []).length ? skuChecked : [SHOW_ALL_ACTIVITY_LOGS],
  outerKind: 'sku',
});

/** Highest parent scope ids from the tree (property / org roots). */
export const highestParentScopeIds = (model: ActivityGroupsModel): string[] => (
  (model.skuHierarchy || [])
    .filter((root) => !root.isObjectGroup)
    .map((root) => root.id)
    .filter(Boolean)
);

/** Departments + up to three custom highest-parent scopes for balance charts. */
export const balanceMetricsScopeSelection = (
  model: ActivityGroupsModel,
  customParentScopeIds: string[],
): MetricsScopeSelection => {
  const scopes = new Set<string>([DEPARTMENTS_METRIC_SCOPE_ID]);
  const skus = new Set<string>([SHOW_ALL_ACTIVITY_LOGS]);
  customParentScopeIds.slice(0, MAX_CUSTOM_BALANCE_SCOPES).forEach((scopeId) => {
    const pick = selectionForParentScope(model, scopeId);
    if (!pick) return;
    pick.scopesChecked.forEach((k) => scopes.add(k));
    pick.skuChecked.forEach((k) => skus.add(k));
  });
  return {
    scopesChecked: [...scopes],
    skuChecked: [...skus],
  };
};

/** All scopes + SKUs — walk-ins / all audits style counts. */
export const allActivityMetricsScopeSelection = (
  _model: ActivityGroupsModel,
): MetricsScopeSelection => ({
  scopesChecked: [ALL_ACTIVITY_SCOPE_ID],
  skuChecked: [SHOW_ALL_ACTIVITY_LOGS],
});

export const resolveMetricsScopeSelection = (
  metricKey: MetricsKey,
  model: ActivityGroupsModel | null | undefined,
  customBalanceScopeIds: string[],
  logsSelection?: { scopesChecked: string[], skuChecked: string[] },
): MetricsScopeSelection => {
  if (!model?.groups.length) {
    return { scopesChecked: [ALL_ACTIVITY_SCOPE_ID], skuChecked: [SHOW_ALL_ACTIVITY_LOGS] };
  }
  if (metricKey === 'Active audits' || metricKey === 'Active audits2') {
    return logsMetricsScopeSelection(
      logsSelection?.scopesChecked || [],
      logsSelection?.skuChecked || [],
    );
  }
  if (metricKey === 'Departments') {
    return departmentMetricsScopeSelection();
  }
  if (metricKey === 'All balances') {
    if (!customBalanceScopeIds.length) {
      return allActivityMetricsScopeSelection(model);
    }
    return balanceMetricsScopeSelection(model, customBalanceScopeIds);
  }
  if (metricKey === 'Employees' || metricKey === 'All audits') {
    return allActivityMetricsScopeSelection(model);
  }
  return allActivityMetricsScopeSelection(model);
};

export { MAX_CUSTOM_BALANCE_SCOPES, DEPARTMENTS_METRIC_SCOPE_ID };
